import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import '@/styles/InitiativeTracker/InitiativeCard.less';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/assets/icons/LibrarySvg';
const InitiativeCard = (props) => {
    var _a;
    const { card, onDelete, onUpdate, onSelect, onOpenDetails, isSelected, onSetMultiplyDamage, onSetDivideDamage, className, isOwnerUser = false, showInitiative = true, userSettings, } = props;
    // Убираем локальное inline-редактирование — используем общий сайдбар.
    const [displayedHp, setDisplayedHp] = useState(card.health);
    const [displayedTempHp, setDisplayedTempHp] = useState(card.tempHealth || 0);
    const { t } = useTranslation();
    // control visibility override after color selection
    const [isGettingDamage, setIsGettingDamage] = useState(false);
    const [isGettingHeal, setIsGettingHeal] = useState(false);
    // per-card color (local only, persisted to localStorage)
    const [cardColor, setCardColor] = useState(null);
    const colorInputRef = useRef(null);
    const swatchBtnRef = useRef(null);
    // Load color from localStorage for this card
    useEffect(() => {
        try {
            const key = `initiativeCardColor:${card.id}`;
            const stored = localStorage.getItem(key);
            if (stored)
                setCardColor(stored);
        }
        catch (e) {
            // ignore
        }
    }, [card.id]);
    // Save color helper
    const persistCardColor = (color) => {
        try {
            const key = `initiativeCardColor:${card.id}`;
            if (color) {
                localStorage.setItem(key, color);
            }
            else {
                localStorage.removeItem(key);
            }
        }
        catch (e) {
            // ignore
        }
    };
    let displayPlayerCardHpAcSetting = true;
    let displayEnemyCardHpAcSetting = true;
    let displayCardStatusSetting = true;
    if (userSettings) {
        displayPlayerCardHpAcSetting = userSettings.charactersSettings.showPlayersHpAc;
        displayEnemyCardHpAcSetting = userSettings.charactersSettings.showEnemiesHpAc;
        displayCardStatusSetting = userSettings.charactersSettings.showCharactersStatus;
    }
    let displayCardHpAcSetting = displayPlayerCardHpAcSetting || displayEnemyCardHpAcSetting;
    if ((_a = card.characterType) === null || _a === void 0 ? void 0 : _a.includes('player')) {
        displayCardHpAcSetting = !!displayPlayerCardHpAcSetting;
    }
    else {
        displayCardHpAcSetting = !!displayEnemyCardHpAcSetting;
    }
    // Animate HP changes.
    useEffect(() => {
        let frame;
        if (displayedHp === card.health && displayedTempHp === card.tempHealth) {
            setTimeout(() => setIsGettingDamage(false), 250);
            setTimeout(() => setIsGettingHeal(false), 1040);
            return;
        }
        // If getting damage, set class
        if (card.health < displayedHp && !isGettingDamage)
            setIsGettingDamage(true);
        if (card.tempHealth < displayedTempHp && !isGettingDamage)
            setIsGettingDamage(true);
        if (card.health > displayedHp && !isGettingHeal)
            setIsGettingHeal(true);
        const step = () => {
            setTimeout(() => {
                // setTimeout: set displayedHp after animation keyframes sec.
                setDisplayedTempHp((prev) => {
                    if (prev === card.tempHealth)
                        return prev;
                    const diff = card.tempHealth - prev;
                    const delta = Math.abs(diff) > 50 ? Math.sign(diff) * 2 : Math.sign(diff);
                    const next = prev + delta;
                    if ((delta > 0 && next > card.tempHealth) || (delta < 0 && next < card.tempHealth)) {
                        return card.tempHealth;
                    }
                    return next;
                });
                setDisplayedHp((prev) => {
                    if (prev === card.health)
                        return prev;
                    const diff = card.health - prev;
                    const delta = Math.abs(diff) > 50 ? Math.sign(diff) * 2 : Math.sign(diff);
                    const next = prev + delta;
                    if ((delta > 0 && next > card.health) || (delta < 0 && next < card.health)) {
                        return card.health;
                    }
                    return next;
                });
                setTimeout(() => setIsGettingDamage(false), 250);
                setTimeout(() => setIsGettingHeal(false), 1040);
            }, 250);
            frame = requestAnimationFrame(step);
        };
        frame = requestAnimationFrame(step);
        return () => cancelAnimationFrame(frame);
    }, [card.health, card.tempHealth, displayedHp, displayedTempHp, isGettingDamage, isGettingHeal]);
    // Устанавливает открытие общей боковой панели редактирования
    const handleSetEdit = () => {
        if (onOpenDetails) {
            onOpenDetails(card);
            return;
        }
    };
    return (_jsxs("div", { "data-character-id": card.id, className: classNames('initiative-card', {
            active: card.isActive,
            selected: isSelected,
            editing: false,
            'half-health-status': card.health <= card.maxHealth / 2,
            'getting-damage': isGettingDamage,
            'getting-heal': isGettingHeal,
        }, className), onClick: (event) => onSelect(card.id, event), children: [_jsxs("div", { className: `card-wrapper ${card.health <= 0 ? 'dead' : ''}`, style: cardColor ? { borderTop: `8px solid ${cardColor}` } : undefined, children: [_jsxs("div", { className: "card-header", children: [onDelete && onUpdate && isOwnerUser && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "card-buttons", children: [onSetMultiplyDamage && (_jsx("button", { type: "button", className: `multiply-damage-checkbox ${card.multiplyDamage ? 'checked' : ''}`, "aria-pressed": card.multiplyDamage ? 'true' : 'false', onClick: (e) => {
                                                    e.stopPropagation();
                                                    onSetMultiplyDamage === null || onSetMultiplyDamage === void 0 ? void 0 : onSetMultiplyDamage(card, !card.multiplyDamage);
                                                }, title: "Take x2 damage", children: _jsx(Icon, { name: "shield" }) })), onSetDivideDamage && (_jsx("button", { type: "button", className: `divide-damage-checkbox ${card.divideDamage ? 'checked' : ''}`, "aria-pressed": card.divideDamage ? 'true' : 'false', onClick: (e) => {
                                                    e.stopPropagation();
                                                    onSetDivideDamage === null || onSetDivideDamage === void 0 ? void 0 : onSetDivideDamage(card, !card.divideDamage);
                                                }, title: "Take half damage", children: _jsx(Icon, { name: "shield" }) })), _jsx("div", { className: "btn-col edit-col", children: _jsx("button", { className: "edit-btn", onClick: (e) => {
                                                        e.stopPropagation();
                                                        handleSetEdit();
                                                    }, children: _jsx(Icon, { name: "pencil" }) }) }), _jsx("div", { className: "btn-col delete-col", children: _jsx("button", { className: "delete-btn", onClick: (e) => {
                                                        e.stopPropagation();
                                                        onDelete(card);
                                                    }, children: _jsx(Icon, { name: "cross" }) }) })] }), _jsx("div", { className: "color-col", children: _jsxs("div", { className: "color-control", role: "group", "aria-label": "Color controls", children: [_jsx("button", { className: "color-swatch-btn", ref: swatchBtnRef, "data-has-color": !!cardColor, onClick: (e) => {
                                                        var _a;
                                                        e.stopPropagation();
                                                        // trigger hidden color input
                                                        (_a = colorInputRef.current) === null || _a === void 0 ? void 0 : _a.click();
                                                    }, title: cardColor ? 'Change card color' : 'Set card color', "aria-label": "Set card color", style: {
                                                        background: cardColor || 'transparent',
                                                        border: cardColor ? '1px solid rgba(0,0,0,0.15)' : undefined,
                                                    }, children: cardColor ? (_jsx("span", { className: "swatch-placeholder", "aria-hidden": "true", style: { background: cardColor } })) : (_jsx(Icon, { name: "palette" })) }), cardColor && (_jsxs(_Fragment, { children: [_jsx("span", { className: "color-separator", "aria-hidden": "true" }), _jsx("button", { className: "clear-color-btn", onClick: (e) => {
                                                                e.stopPropagation();
                                                                setCardColor(null);
                                                                persistCardColor(null);
                                                            }, title: "Clear color", "aria-label": "Clear color", children: _jsx(Icon, { name: "cross" }) })] })), _jsx("input", { className: "color-input", ref: colorInputRef, type: "color", value: cardColor !== null && cardColor !== void 0 ? cardColor : '#ffffff', onChange: (e) => {
                                                        var _a;
                                                        const c = e.target.value;
                                                        setCardColor(c);
                                                        persistCardColor(c);
                                                        (_a = swatchBtnRef.current) === null || _a === void 0 ? void 0 : _a.blur();
                                                    }, onClick: (e) => e.stopPropagation(), style: { display: 'none' } })] }) })] })), card.health <= card.maxHealth / 2 && card.health > 0 && displayCardStatusSetting && (_jsx("div", { className: "half-health-warning", children: _jsx(Icon, { name: "heart_damaged_2" }) })), (displayCardHpAcSetting || isOwnerUser) && (_jsxs("div", { className: "character-hp-ac-container", children: [_jsxs("div", { className: "character-ac-container", children: [_jsx("span", { children: card.armorClass }), _jsx(Icon, { name: "shield" })] }), displayedTempHp > 0 && (_jsx("div", { className: "character-hp-container character-temp-hp-container", children: _jsxs("span", { children: ["+", displayedTempHp] }) })), _jsxs("div", { className: "character-hp-container", children: [_jsx("span", { children: displayedHp }), displayedHp > 0 ? _jsx(Icon, { name: "heart" }) : _jsx(Icon, { name: "broken_heart" })] })] })), _jsx("div", { className: "character-image-container", children: card.imageUrl ? (_jsx("img", { src: card.imageUrl, className: "character-image", alt: "", title: "" })) : (_jsx(Icon, { name: "goblin_1" })) })] }), ' ', _jsxs("div", { className: "card-body", children: [_jsx("span", { className: "character-name", children: card.name }), _jsx("span", { className: "character-notes", children: card.description })] }), ' ', showInitiative && (_jsx("div", { className: "card-footer", children: _jsxs("span", { children: [t('character.initiative'), ": ", card.initiative] }) }))] }), ' '] }));
};
export default InitiativeCard;
